import { useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { ageDepuisNaissance } from '../lib/format.js';
import { useTheme, THEMES } from '../lib/theme.jsx';
import { Laterale } from '../components/Laterale.jsx';

/**
 * Préférences du compte.
 *
 * Elles servent de valeurs par défaut à la création d'un voyage : la date de
 * naissance évite de se ressaisir soi-même à chaque fois, la ville pré-remplit
 * le départ. Ce sont des défauts, jamais des contraintes — le formulaire de
 * voyage reste libre de les contredire.
 *
 * La date de naissance plutôt que l'âge : un âge saisi une fois devient faux
 * au premier anniversaire, sans que rien ne le signale.
 */

/** Aujourd'hui en « AAAA-MM-JJ », borne haute du champ de naissance. */
const aujourdHui = () => new Date().toISOString().slice(0, 10);
export function Preferences() {
  const { user, enregistrerPreferences } = useAuth();
  const { theme, choisirTheme } = useTheme();

  const [erreurTheme, setErreurTheme] = useState(null);
  const [champs, setChamps] = useState({
    birthDate: user.birthDate ?? '',
    city: user.city ?? '',
  });
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [enregistre, setEnregistre] = useState(false);

  const modifier = (champ) => (evenement) => {
    setEnregistre(false);
    setChamps((precedent) => ({ ...precedent, [champ]: evenement.target.value }));
  };

  const age = ageDepuisNaissance(champs.birthDate);

  /**
   * Le thème s'applique au clic, sans passer par « Enregistrer » : un réglage
   * d'apparence se juge en le voyant, et le valider à l'aveugle n'aurait pas
   * de sens. L'enregistrement dans le compte suit, et son échec ne défait pas
   * ce qui est déjà à l'écran — il se contente de le dire.
   */
  async function changerTheme(choisi) {
    setErreurTheme(null);
    choisirTheme(choisi);

    try {
      await enregistrerPreferences({ theme: choisi });
    } catch {
      setErreurTheme(
        'Le thème est appliqué sur cet appareil, mais n’a pas pu être '
        + 'enregistré sur votre compte : il ne suivra pas sur vos autres écrans.',
      );
    }
  }

  async function soumettre(evenement) {
    evenement.preventDefault();
    setErreur(null);
    setEnvoi(true);

    try {
      await enregistrerPreferences({
        birthDate: champs.birthDate || null,
        city: champs.city.trim() || null,
      });
      setEnregistre(true);
    } catch (echec) {
      setErreur(echec.message);
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div className="cadre">
      <Laterale actif="preferences" />

      <main>
        <header className="entete">
          <div>
            <h1>Préférences</h1>
            <p className="entete__sous-titre">{user.email}</p>
          </div>
        </header>

        <section className="verre page--etroite">
          <form onSubmit={soumettre} noValidate>
            <div className="champ">
              <label htmlFor="birthDate">Votre date de naissance</label>
              <input
                id="birthDate"
                type="date"
                max={aujourdHui()}
                autoComplete="bday"
                value={champs.birthDate}
                onChange={modifier('birthDate')}
              />
              <p className="aide">
                Reprise quand vous êtes ajouté comme voyageur : les suggestions
                tiennent compte des âges du groupe.
                {/* L'âge calculé confirme la saisie : une date tapée au clavier
                    se trompe facilement d'une décennie, et l'écart saute alors
                    aux yeux. */}
                {age !== null && ` Vous avez ${age} ans.`}
              </p>
            </div>

            <div className="champ">
              <label htmlFor="city">Votre ville</label>
              <input
                id="city"
                type="text"
                placeholder="facultatif"
                value={champs.city}
                onChange={modifier('city')}
              />
              <p className="aide">
                Pré-remplit la ville de départ d’un nouveau voyage. Vous pourrez
                toujours la changer.
              </p>
            </div>

            {erreur && (
              <p className="erreur" role="alert">
                {erreur}
              </p>
            )}

            <div className="actions">
              <button className="bouton bouton--auto" type="submit" disabled={envoi}>
                {envoi ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              {enregistre && (
                <span className="confirmation-breve" role="status">
                  Préférences enregistrées
                </span>
              )}
            </div>
          </form>
        </section>

        <section className="verre page--etroite section--suivante">
          <h2>Apparence</h2>
          <fieldset className="groupe">
            <legend className="invisible">Thème d’affichage</legend>
            <div className="etiquettes">
              {THEMES.map(([code, libelle]) => (
                <label
                  key={code}
                  className={`etiquette${theme === code ? ' etiquette--active' : ''}`}
                >
                  <input
                    type="radio"
                    name="theme"
                    value={code}
                    checked={theme === code}
                    onChange={() => changerTheme(code)}
                  />
                  {libelle}
                </label>
              ))}
            </div>
            <p className="aide">
              « Selon l’appareil » suit le réglage clair ou sombre de votre
              téléphone ou de votre ordinateur, y compris s’il bascule tout seul
              le soir. Le choix suit votre compte d’un écran à l’autre.
            </p>
          </fieldset>

          {erreurTheme && (
            <p className="erreur" role="alert">
              {erreurTheme}
            </p>
          )}
        </section>
      </main>
    </div>
  );
}
